"use client";
import type {
  NavigateOptions,
  PrefetchOptions,
} from "next/dist/shared/lib/app-router-context.shared-runtime";
import Form from "next/form";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { type ComponentProps, useCallback, useMemo } from "react";
import { useLocale, useRouting } from "../client/client";
import { replaceLocale, safeLocale } from "../shared";
type SafeLocaleFn = <T extends string | undefined>(locale: T) => T;

export const useGetSafeLocale = () => {
  const routing = useRouting();

  const getSafeLocale = useCallback(
    safeLocale.bind(null, routing) as SafeLocaleFn,
    [routing]
  );
  return getSafeLocale;
};

const useLocalizedPathname = () => {
  const locale = useLocale();
  const originalPathname = usePathname();
  const routing = useRouting();

  const localizedPathname = replaceLocale(
    originalPathname,
    locale,
    routing,
    undefined
  );

  return localizedPathname;
};

export interface LocalizedNavigateOptions extends NavigateOptions {
  locale?: string;
}

export interface LocalizedPrefetchOptions extends PrefetchOptions {
  locale?: string;
}

const useLocalizedRouter = () => {
  const getSafeLocale = useGetSafeLocale();
  const locale = useLocale();
  const originalRouter = useRouter();
  const routing = useRouting();

  const push = useCallback(
    (url: string, options?: LocalizedNavigateOptions) => {
      const localeToUse = getSafeLocale(options?.locale || locale);
      const overrideLocale = getSafeLocale(options?.locale);
      return originalRouter.push(
        replaceLocale(url, localeToUse, routing, overrideLocale),
        options
      );
    },
    [originalRouter]
  );

  const replace = useCallback(
    (url: string, options?: LocalizedNavigateOptions) => {
      const localeToUse = getSafeLocale(options?.locale || locale);
      const overrideLocale = getSafeLocale(options?.locale);
      return originalRouter.replace(
        replaceLocale(url, localeToUse, routing, overrideLocale),
        options
      );
    },
    [originalRouter]
  );

  const prefetch = useCallback(
    (url: string, options?: LocalizedPrefetchOptions) => {
      const localeToUse = getSafeLocale(options?.locale || locale);
      const overrideLocale = getSafeLocale(options?.locale);

      return originalRouter.prefetch(
        replaceLocale(url, localeToUse, routing, overrideLocale),
        options
      );
    },
    [originalRouter]
  );

  return {
    ...originalRouter,
    push,
    replace,
    prefetch,
  };
};

export const LocalizedLink = ({
  href,
  locale: propsLocale,
  ...props
}: ComponentProps<typeof Link> & { locale?: string }) => {
  const locale = useLocale();
  const getSafeLocale = useGetSafeLocale();
  const routing = useRouting();
  const hrefString = href.toString();

  const replaced = useMemo(() => {
    return replaceLocale(
      href.toString(),
      locale,
      routing,
      getSafeLocale(propsLocale)
    );
  }, [href, propsLocale]);

  if (
    hrefString.startsWith("http://") ||
    hrefString.startsWith("https://") ||
    hrefString.startsWith("mailto:")
  ) {
    return <Link href={href} {...props} />;
  }

  return <Link href={replaced} {...props} />;
};

export const LocalizedForm = ({
  action,
  locale: propsLocale,
  ...props
}: ComponentProps<typeof Form> & { locale?: string }) => {
  const locale = useLocale();
  const getSafeLocale = useGetSafeLocale();
  const routing = useRouting();
  const replacedAction = useMemo(() => {
    if (typeof action === "string") {
      return replaceLocale(action, locale, routing, getSafeLocale(propsLocale));
    } else {
      return action;
    }
  }, [action, propsLocale]);

  return <Form action={replacedAction} {...props} />;
};

export {
  LocalizedForm as Form,
  LocalizedLink as Link,
  useLocalizedPathname as usePathname,
  useLocalizedRouter as useRouter,
};
